import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Form, Input, Modal, Spin, Tag } from 'antd'
import type { InvitePayload, InviteResult, Organization, OrgSelection } from '../../types'
import {
  buildOrgSelections,
  canAddUsersToOrganization,
  formatInviteFailureMessage,
  formatInviteSuccessMessage,
} from '../../utils'
import NoticeBanner from '../shared/NoticeBanner'
import OrgRolePicker from '../shared/OrgRolePicker'

interface InviteUserModalProps {
  open: boolean
  organizations: Organization[]
  defaultCheckedOrgId?: string
  loading?: boolean
  submitting?: boolean
  currentCustomerId?: string | null
  onCancel: () => void
  onSubmit: (payload: InvitePayload) => Promise<InviteResult[] | void>
  onUpgrade?: (organization: Organization) => void
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  open,
  organizations,
  defaultCheckedOrgId,
  loading = false,
  submitting = false,
  currentCustomerId,
  onCancel,
  onSubmit,
  onUpgrade,
}) => {
  const [form] = Form.useForm<{ firstName: string; lastName: string; email: string; nmls: string }>()
  const [orgSelections, setOrgSelections] = useState<Record<string, OrgSelection>>({})
  const [completedByOrg, setCompletedByOrg] = useState<Record<string, InviteResult>>({})
  const [warning, setWarning] = useState('')
  const [invalidRoleOrgIds, setInvalidRoleOrgIds] = useState<string[]>([])

  useEffect(() => {
    if (!open) return
    form.resetFields()
    setWarning('')
    setInvalidRoleOrgIds([])
    setCompletedByOrg({})
  }, [open, form])

  useEffect(() => {
    if (!open) return
    setOrgSelections(buildOrgSelections(
      organizations,
      undefined,
      defaultCheckedOrgId && organizations.some((org) => org.id === defaultCheckedOrgId && canAddUsersToOrganization(org))
        ? defaultCheckedOrgId
        : organizations.find(canAddUsersToOrganization)?.id,
    ))
    // Rebuild when the modal opens or when managed-organizations finishes loading.
    // Omit `organizations` so a later seat refresh does not reset checked orgs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading])

  const completedOrgIds = useMemo(() => Object.keys(completedByOrg), [completedByOrg])
  const successResults = useMemo(
    () => Object.values(completedByOrg).filter((result) => result.status !== 'Error'),
    [completedByOrg],
  )
  const hasPendingInvite = Object.entries(orgSelections).some(([orgId, selection]) => {
    if (!selection.checked || completedByOrg[orgId]) return false
    const org = organizations.find((item) => item.id === orgId)
    return canAddUsersToOrganization(org)
  })

  const handleSubmit = async () => {
    const values = await form.validateFields()
    const pendingSelections = Object.entries(orgSelections).filter(([orgId, selection]) => {
      if (!selection.checked || completedByOrg[orgId]) return false
      const org = organizations.find((item) => item.id === orgId)
      return canAddUsersToOrganization(org)
    })
    const missingRoleSelections = pendingSelections.filter(([, selection]) => selection.roles.length === 0)
    const missingRoleOrgs = missingRoleSelections
      .map(([orgId]) => organizations.find((item) => item.id === orgId)?.name || orgId)
    if (missingRoleOrgs.length > 0) {
      setInvalidRoleOrgIds(missingRoleSelections.map(([orgId]) => orgId))
      setWarning(`Select at least one role for ${missingRoleOrgs.join(', ')}.`)
      return
    }
    setInvalidRoleOrgIds([])

    const roles = Object.fromEntries(
      pendingSelections.map(([orgId, selection]) => [orgId, selection.roles]),
    )
    const results = await onSubmit({
      firstName: values.firstName?.trim() || '',
      lastName: values.lastName?.trim() || '',
      email: values.email?.trim().toLowerCase() || '',
      nmls: values.nmls?.trim() || '',
      roles,
    })

    const settledResults = (results || []).filter((result) => result.status !== 'Error')
    if (settledResults.length > 0) {
      setCompletedByOrg((prev) => {
        const next = { ...prev }
        settledResults.forEach((result) => {
          next[result.customerId] = result
        })
        return next
      })
      setOrgSelections((prev) => {
        const next = { ...prev }
        settledResults.forEach((result) => {
          if (!next[result.customerId]) return
          next[result.customerId] = { ...next[result.customerId], checked: true }
        })
        return next
      })
    }

    const failedResults = (results || []).filter((result) => result.status === 'Error')
    setWarning(failedResults.length > 0 ? formatInviteFailureMessage(failedResults, organizations) : '')
  }

  return (
    <Modal
      open={open}
      title="Invite users on your team"
      onCancel={onCancel}
      footer={null}
      width={640}
      className="um-refactor__modal"
      destroyOnClose
    >
      <p className="um-refactor__modal-desc">
        Send an invitation to members on your team via email.
      </p>

      <Form form={form} layout="vertical" requiredMark={false}>
        <div className="um-refactor__field-row">
          <Form.Item name="firstName" label="First name" className="um-refactor__form-item" rules={[{ required: true, message: 'First name is required' }]}>
            <Input size="large" placeholder="Enter first name" />
          </Form.Item>
          <Form.Item name="lastName" label="Last name" className="um-refactor__form-item" rules={[{ required: true, message: 'Last name is required' }]}>
            <Input size="large" placeholder="Enter last name" />
          </Form.Item>
        </div>
        <Form.Item name="email" label="Email address" rules={[{ required: true, type: 'email', message: 'Enter a valid email address' }]}>
          <Input size="large" placeholder="Enter loan officer email address" />
        </Form.Item>
        <Form.Item name="nmls" label="NMLS number">
          <Input size="large" placeholder="Enter loan officer NMLS number" />
        </Form.Item>
      </Form>

      <div className="um-refactor__section-label">
        <span className="um-refactor__field-label" style={{ marginBottom: 0 }}>
          Organizations &amp; role
        </span>
        <Tag color="blue" className="um-refactor__new-badge">
          NEW
        </Tag>
      </div>
      <p className="um-refactor__section-hint">
        Choose which of your organizations this person joins. The role is set independently per organization.
      </p>

      <Spin spinning={loading} wrapperClassName="um-refactor__picker-spin">
        <OrgRolePicker
          organizations={organizations}
          selections={orgSelections}
          restrictAdds
          upgradeIntent="invite"
          currentCustomerId={currentCustomerId}
          disabledOrgIds={completedOrgIds}
          allowEmptyRoles
          invalidRoleOrgIds={invalidRoleOrgIds}
          onUpgrade={onUpgrade}
          onChange={(orgId, patch) =>
            setOrgSelections((prev) => {
              if (completedByOrg[orgId]) return prev
              const org = organizations.find((item) => item.id === orgId)
              if (patch.checked && !canAddUsersToOrganization(org)) return prev
              setWarning('')
              setInvalidRoleOrgIds((ids) => ids.filter((id) => id !== orgId))
              return { ...prev, [orgId]: { ...prev[orgId], ...patch } }
            })
          }
        />
      </Spin>

      {successResults.length > 0 && (
        <Alert
          className="um-refactor__notice um-refactor__notice--success"
          type="success"
          message={<span style={{ whiteSpace: 'pre-line' }}>{formatInviteSuccessMessage(successResults, organizations)}</span>}
          showIcon
        />
      )}

      {warning && (
        <Alert
          className="um-refactor__notice um-refactor__notice--warn"
          type="warning"
          message={<span style={{ whiteSpace: 'pre-line' }}>{warning}</span>}
          showIcon
        />
      )}

      <NoticeBanner variant="info">
        If this email already has a Zeitro account, it will be <b>linked</b> to the selected organizations instead of creating a new account.
      </NoticeBanner>

      <Button
        type="primary"
        block
        size="large"
        loading={submitting}
        disabled={loading || !hasPendingInvite}
        className="um-refactor__btn-primary--full"
        onClick={handleSubmit}
      >
        Send invite
      </Button>
    </Modal>
  )
}

export default InviteUserModal
